import React, { useState, useEffect } from "react";
import { useOrganization } from "../contexts/OrganizationContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Stack,
  Divider,
  CircularProgress,
  FormControl,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  Chip,
  Paper,
  SelectChangeEvent,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  Email as EmailIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Visibility,
  VisibilityOff,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface EmailConfig {
  enabled: boolean;
  configured: boolean;
  provider: 'smtp' | 'resend' | 'aws-ses' | 'brevo';
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  fromEmail: string;
  fromName: string;
  replyToEmail?: string;
  hasResendApiKey?: boolean;
  awsAccessKeyId?: string;
  awsSecretAccessKey?: string;
  awsRegion?: string;
  hasBrevoApiKey?: boolean;
}

interface EmailPreferences {
  sendTournamentInvitation?: boolean;
  sendTournamentConfirmation: boolean;
  sendTournamentWaitlist: boolean;
  sendWaitlistPromotion: boolean;
  sendTournamentReminder: boolean;
  sendTournamentResults: boolean;
  sendLeagueDayConfirmation: boolean;
  sendLeagueDayReminder: boolean;
  sendLeagueDayResults: boolean;
  sendSeasonRegistrationConfirmation: boolean;
  sendWelcomeEmail: boolean;
  sendPasswordReset: boolean;
  notifyAdminOnRegistration: boolean;
  adminNotificationEmail?: string;
  sendPaymentReminder: boolean;
}

type EmailTemplateType =
  | "tournament_invite"
  | "tournament_invite_accepted"
  | "tournament_invite_declined"
  | "tournament_confirmation"
  | "tournament_waitlist"
  | "waitlist_promotion"
  | "tournament_reminder"
  | "tournament_results"
  | "league_day_confirmation"
  | "league_day_reminder"
  | "league_day_results"
  | "season_registration_confirmation"
  | "season_registration_bulk"
  | "welcome"
  | "password_reset"
  | "admin_new_player_notification"
  | "payment_reminder"
  | "season_invitation"
  | "whatsapp_league_invite";

interface EmailTemplate {
  type: EmailTemplateType;
  subject: string;
  htmlBody: string;
  description?: string;
  availableVariables?: string[];
}

const TEMPLATE_LABELS: Record<EmailTemplateType, string> = {
  tournament_invite: "Tournament Invitation",
  tournament_invite_accepted: "Invitation Accepted",
  tournament_invite_declined: "Invitation Declined",
  tournament_confirmation: "Tournament Confirmation",
  tournament_waitlist: "Tournament Waitlist",
  waitlist_promotion: "Waitlist Promotion",
  tournament_reminder: "Tournament Reminder",
  tournament_results: "Tournament Results",
  league_day_confirmation: "League Day Confirmation",
  league_day_reminder: "League Day Reminder",
  league_day_results: "League Day Results",
  season_registration_confirmation: "Season Registration Confirmation",
  season_registration_bulk: "Season Registration Bulk Email",
  welcome: "Welcome Email",
  password_reset: "Password Reset",
  admin_new_player_notification: "Admin - New Player Notification",
  payment_reminder: "Payment Reminder",
  season_invitation: "Season Invitation",
  whatsapp_league_invite: "Whatsapp league invite",
};

// Templates that only make sense for league organizations
const LEAGUE_ONLY_TEMPLATES: EmailTemplateType[] = [
  "league_day_confirmation",
  "league_day_reminder",
  "league_day_results",
  "season_registration_confirmation",
  "season_registration_bulk",
  "season_invitation",
  "payment_reminder",
  "whatsapp_league_invite",
];

export default function EmailSettings() {
  const { currentOrg } = useOrganization();
  const isTournamentOrg = currentOrg?.competitionType === "tournament";
  const visibleTemplateLabels = Object.entries(TEMPLATE_LABELS).filter(
    ([value]) => !isTournamentOrg || !LEAGUE_ONLY_TEMPLATES.includes(value as EmailTemplateType),
  );
  const [tabValue, setTabValue] = useState(0);
  const [optOutPlayers, setOptOutPlayers] = useState<
    { id: string; name: string; email: string; division: string; optedOut: string[] }[]
  >([]);
  const [optOutsLoading, setOptOutsLoading] = useState(false);

  useEffect(() => {
    if (tabValue !== 4) return;
    setOptOutsLoading(true);
    apiRequest("/api/email-config/opt-outs")
      .then((r) => (r.ok ? r.json() : { players: [] }))
      .then((d) => setOptOutPlayers(d.players || []))
      .catch((err) => console.error("Error loading opt-outs:", err))
      .finally(() => setOptOutsLoading(false));
  }, [tabValue]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoDialogContent, setInfoDialogContent] = useState({ title: "", description: "" });
  const [message, setMessage] = useState<{
    type: "success" | "error" | "info";
    text: string;
  } | null>(null);

  // Template editor state
  const [selectedTemplateType, setSelectedTemplateType] = useState<EmailTemplateType>("tournament_confirmation");
  const [currentTemplate, setCurrentTemplate] = useState<EmailTemplate | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  // Email usage stats
  const [usageStats, setUsageStats] = useState<{
    count: number;
    limit: number;
    limitType: string;
    percentUsed: number;
    remaining: number;
    nearLimit: boolean;
    atLimit: boolean;
  } | null>(null);

  // Email configuration state
  const [config, setConfig] = useState<EmailConfig>({
    enabled: false,
    configured: false,
    provider: 'resend',
    smtpHost: "smtp.gmail.com",
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: "",
    fromEmail: "",
    fromName: "",
    replyToEmail: "",
  });
  const [smtpPass, setSmtpPass] = useState("");
  const [resendApiKey, setResendApiKey] = useState("");
  const [brevoApiKey, setBrevoApiKey] = useState("");
  const [awsAccessKeyId, setAwsAccessKeyId] = useState("");
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState("");
  const [awsRegion, setAwsRegion] = useState("us-east-1");

  // Email preferences state
  const [preferences, setPreferences] = useState<EmailPreferences>({
    sendTournamentConfirmation: true,
    sendTournamentWaitlist: true,
    sendWaitlistPromotion: true,
    sendTournamentReminder: false,
    sendTournamentResults: false,
    sendLeagueDayConfirmation: true,
    sendLeagueDayReminder: false,
    sendLeagueDayResults: false,
    sendSeasonRegistrationConfirmation: true,
    sendWelcomeEmail: true,
    sendPasswordReset: true,
    notifyAdminOnRegistration: false,
    adminNotificationEmail: "",
    sendPaymentReminder: true,
  });

  // Custom email state
  const [customEmailSubject, setCustomEmailSubject] = useState("");
  const [customEmailBody, setCustomEmailBody] = useState("");
  const [customEmailTemplateType, setCustomEmailTemplateType] = useState<EmailTemplateType | "">("");
  const [customEmailTargetType, setCustomEmailTargetType] = useState<"all" | "specific" | "division">("all");
  const [customEmailSelectedPlayers, setCustomEmailSelectedPlayers] = useState<any[]>([]);
  const [customEmailDivision, setCustomEmailDivision] = useState<string | null>(null);
  const [customEmailGuestFilter, setCustomEmailGuestFilter] = useState<"all" | "guestOnly" | "nonGuestOnly">("all");
  const [customEmailPreviewOpen, setCustomEmailPreviewOpen] = useState(false);
  const [customEmailRecipients, setCustomEmailRecipients] = useState<any[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false);
  const [allPlayers, setAllPlayers] = useState<any[]>([]);
  const [divisions, setDivisions] = useState<string[]>([]);

  useEffect(() => {
    fetchEmailConfig();
    fetchEmailPreferences();
    fetchEmailUsage();
  }, []);

  const fetchEmailConfig = async () => {
    try {
      const response = await apiRequest("/api/email-config/config");
      const data = await response.json();
      
      // Merge with existing config to preserve defaults
      setConfig(prevConfig => ({
        ...prevConfig,
        ...data,
      }));
    } catch (error) {
      console.error("Error fetching email config:", error);
      showMessage("error", "Failed to load email configuration");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailPreferences = async () => {
    try {
      const response = await apiRequest("/api/email-config/preferences");
      const data = await response.json();
      setPreferences(data);
    } catch (error) {
      console.error("Error fetching email preferences:", error);
    }
  };

  const fetchEmailUsage = async () => {
    try {
      const response = await apiRequest("/api/email-config/usage");
      if (response.ok) {
        const data = await response.json();
        setUsageStats(data);
      }
    } catch (error) {
      console.error("Error fetching email usage:", error);
      // Don't show error message, usage stats are optional
    }
  };

  const showMessage = (
    type: "success" | "error" | "info",
    text: string
  ) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      // Build config object, excluding empty optional fields
      const configToSave: any = {
        enabled: config.enabled,
        provider: config.provider,
        fromEmail: config.fromEmail,
        fromName: config.fromName,
      };

      // SMTP settings (only if provider is smtp)
      if (config.provider === 'smtp') {
        configToSave.smtpHost = config.smtpHost;
        configToSave.smtpPort = config.smtpPort;
        configToSave.smtpSecure = config.smtpSecure;
        configToSave.smtpUser = config.smtpUser;
        
        // Only include smtpPass if it was changed
        if (smtpPass) {
          configToSave.smtpPass = smtpPass;
        }
      }

      // Resend settings (only if provider is resend)
      if (config.provider === 'resend' && resendApiKey) {
        configToSave.resendApiKey = resendApiKey;
      }

      // AWS SES settings (only if provider is aws-ses)
      if (config.provider === 'aws-ses') {
        if (awsAccessKeyId) configToSave.awsAccessKeyId = awsAccessKeyId;
        if (awsSecretAccessKey) configToSave.awsSecretAccessKey = awsSecretAccessKey;
        if (awsRegion) configToSave.awsRegion = awsRegion;
      }

      // Brevo settings (only if provider is brevo)
      if (config.provider === 'brevo' && brevoApiKey) {
        configToSave.brevoApiKey = brevoApiKey;
      }

      // Only include replyToEmail if it has a value
      // Only include replyToEmail if it has a value
      if (config.replyToEmail && config.replyToEmail.trim()) {
        configToSave.replyToEmail = config.replyToEmail.trim();
      }

      const response = await apiRequest("/api/email-config/config", {
        method: "PUT",
        body: JSON.stringify(configToSave),
      });

      if (response.ok) {
        showMessage("success", "Email configuration saved successfully!");
        fetchEmailConfig();
      } else {
        const error = await response.json();
        showMessage("error", error.error || "Failed to save configuration");
      }
    } catch (error) {
      showMessage("error", "Failed to save email configuration");
      console.error("Error saving config:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      const response = await apiRequest("/api/email-config/preferences", {
        method: "PUT",
        body: JSON.stringify(preferences),
      });

      if (response.ok) {
        showMessage("success", "Email preferences saved successfully!");
      } else {
        const error = await response.json();
        showMessage("error", error.error || "Failed to save preferences");
      }
    } catch (error) {
      showMessage("error", "Failed to save email preferences");
      console.error("Error saving preferences:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      showMessage("error", "Please enter a test email address");
      return;
    }

    setSendingTest(true);
    try {
      const response = await apiRequest("/api/email-config/test", {
        method: "POST",
        body: JSON.stringify({ testEmail }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showMessage("success", "Test email sent! Check your inbox.");
        setTestDialogOpen(false);
        setTestEmail("");
        // Refresh usage stats
        fetchEmailUsage();
      } else {
        showMessage(
          "error",
          data.error || "Failed to send test email. Check your configuration."
        );
      }
    } catch (error) {
      showMessage("error", "Failed to send test email");
      console.error("Error sending test email:", error);
    } finally {
      setSendingTest(false);
    }
  };

  const handleClearCache = async () => {
    try {
      const response = await apiRequest("/api/email-config/clear-cache", {
        method: "POST",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showMessage("success", "Email cache cleared! Next send will use fresh configuration.");
      } else {
        showMessage("error", data.error || "Failed to clear cache");
      }
    } catch (error) {
      showMessage("error", "Failed to clear cache");
      console.error("Error clearing cache:", error);
    }
  };

  const handleDiagnose = async () => {
    try {
      const response = await apiRequest("/api/email-config/diagnose", {
        method: "GET",
      });

      const data = await response.json();

      if (response.ok) {
        // Format the diagnostics nicely
        let message = `SMTP Diagnostics:\n\n`;
        message += `Host: ${data.smtpHost}\n`;
        message += `Port: ${data.smtpPort}\n\n`;
        message += `Tests:\n`;
        for (const [key, value] of Object.entries(data.tests)) {
          const test = value as any;
          message += `  ${key}: ${test.success ? `✅ Success (${test.time}ms)` : `❌ Failed - ${test.error}`}\n`;
        }
        message += `\n${data.recommendation}`;
        
        alert(message);
        console.log("SMTP Diagnostics:", data);
      } else {
        showMessage("error", data.error || "Failed to run diagnostics");
      }
    } catch (error) {
      showMessage("error", "Failed to run diagnostics");
      console.error("Error running diagnostics:", error);
    }
  };

  const showInfoDialog = (title: string, description: string) => {
    setInfoDialogContent({ title, description });
    setInfoDialogOpen(true);
  };

  // Load specific template
  const loadTemplate = async (templateType: EmailTemplateType) => {
    setLoadingTemplate(true);
    try {
      const response = await apiRequest(`/api/email-config/templates/${templateType}`);
      
      if (response.ok) {
        const data = await response.json();
        setCurrentTemplate(data.template);
      } else {
        const error = await response.json();
        showMessage("error", error.error || "Failed to load template");
      }
    } catch (error) {
      showMessage("error", "Failed to load email template");
      console.error("Error loading template:", error);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!currentTemplate) return;

    setSaving(true);
    try {
      const response = await apiRequest(`/api/email-config/templates/${selectedTemplateType}`, {
        method: "PUT",
        body: JSON.stringify({
          subject: currentTemplate.subject,
          htmlBody: currentTemplate.htmlBody,
        }),
      });

      if (response.ok) {
        showMessage("success", "Email template saved successfully");
      } else {
        const error = await response.json();
        showMessage("error", error.error || "Failed to save template");
      }
    } catch (error) {
      showMessage("error", "Failed to save email template");
      console.error("Error saving template:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = async () => {
    if (!window.confirm("Are you sure you want to reset this template to default? This cannot be undone.")) {
      return;
    }

    setSaving(true);
    try {
      const response = await apiRequest(`/api/email-config/templates/${selectedTemplateType}/reset`, {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentTemplate(data.template);
        showMessage("success", "Template reset to default");
      } else {
        const error = await response.json();
        showMessage("error", error.error || "Failed to reset template");
      }
    } catch (error) {
      showMessage("error", "Failed to reset template");
      console.error("Error resetting template:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateTypeChange = (event: SelectChangeEvent<EmailTemplateType>) => {
    const newType = event.target.value as EmailTemplateType;
    setSelectedTemplateType(newType);
    loadTemplate(newType);
  };

  // Load template when tab changes to templates tab
  useEffect(() => {
    if (tabValue === 2 && !currentTemplate) {
      loadTemplate(selectedTemplateType);
    }
  }, [tabValue]);

  // Load players and divisions when custom email tab is opened
  useEffect(() => {
    if (tabValue === 3) {
      fetchPlayersAndDivisions();
    }
  }, [tabValue]);

  const handleCustomEmailTemplateChange = async (templateType: EmailTemplateType | "") => {
    setCustomEmailTemplateType(templateType);
    
    if (!templateType) {
      // Clear fields if "blank" is selected
      setCustomEmailSubject("");
      setCustomEmailBody("");
      return;
    }

    // Load the template
    try {
      const response = await apiRequest(`/api/email-config/templates/${templateType}`);
      
      if (response.ok) {
        const data = await response.json();
        const template = data.template;
        
        // Populate the fields with template content
        setCustomEmailSubject(template.subject);
        // Convert HTML to plain text for better editing experience
        // Remove HTML tags for simpler editing
        const plainText = template.htmlBody
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n')
          .replace(/<[^>]+>/g, '');
        setCustomEmailBody(plainText);
        
        showMessage("success", `Template "${TEMPLATE_LABELS[templateType]}" loaded. You can now edit and customize it.`);
      } else {
        showMessage("error", "Failed to load template");
      }
    } catch (error) {
      showMessage("error", "Failed to load email template");
      console.error("Error loading template:", error);
    }
  };

  const fetchPlayersAndDivisions = async () => {
    try {
      // Fetch players
      const playersResponse = await apiRequest("/api/players");
      if (playersResponse.ok) {
        const playersData = await playersResponse.json();
        console.log("[Custom Email] Fetched players:", playersData.length);
        
        // Sample first player to see structure
        if (playersData.length > 0) {
          console.log("[Custom Email] Sample player:", {
            name: playersData[0].name,
            isActive: playersData[0].isActive,
            email: playersData[0].email,
            division: playersData[0].division,
            status: playersData[0].status
          });
        }
        
        // Filter active players (show all, some may not have email)
        const activePlayers = playersData.filter((p: any) => p.isActive);
        console.log("[Custom Email] Active players:", activePlayers.length);
        
        const playersWithEmail = activePlayers.filter((p: any) => p.email);
        console.log("[Custom Email] Active players with email:", playersWithEmail.length);
        
        setAllPlayers(activePlayers); // Show all active players

        // Extract unique divisions from ALL active players (not just those with email)
        // This allows targeting divisions even if not all players have emails
        const uniqueDivisions = Array.from(
          new Set(
            activePlayers
              .map((p: any) => p.division)
              .filter(Boolean)
          )
        ) as string[];
        console.log("[Custom Email] Divisions found:", uniqueDivisions);
        console.log("[Custom Email] Note: When sending to a division, only players with email will receive it");
        setDivisions(uniqueDivisions.sort());
      } else {
        console.error("[Custom Email] Failed to fetch players:", playersResponse.status);
      }
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const handlePreviewCustomEmail = async () => {
    if (!customEmailSubject || !customEmailBody) {
      showMessage("error", "Please enter both subject and body");
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await apiRequest("/api/email-config/preview-recipients", {
        method: "POST",
        body: JSON.stringify({
          targetType: customEmailTargetType,
          playerIds: customEmailSelectedPlayers.map(p => p.id),
          division: customEmailDivision,
          guestFilter: customEmailGuestFilter,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCustomEmailRecipients(data.recipients);
        setCustomEmailPreviewOpen(true);
      } else {
        const error = await response.json();
        showMessage("error", error.error || "Failed to preview recipients");
      }
    } catch (error) {
      showMessage("error", "Failed to preview recipients");
      console.error("Error previewing recipients:", error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendCustomEmail = async () => {
    if (!window.confirm(`Send email to ${customEmailRecipients.length} recipients? This cannot be undone.`)) {
      return;
    }

    setSendingCustomEmail(true);
    setCustomEmailPreviewOpen(false);
    try {
      const response = await apiRequest("/api/email-config/send-custom", {
        method: "POST",
        body: JSON.stringify({
          targetType: customEmailTargetType,
          playerIds: customEmailSelectedPlayers.map(p => p.id),
          division: customEmailDivision,
          guestFilter: customEmailGuestFilter,
          subject: customEmailSubject,
          htmlBody: customEmailBody.replace(/\n/g, "<br>"),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.failed > 0 && data.sent === 0) {
          // All emails failed
          showMessage(
            "error",
            `Failed to send emails. ${data.error || 'Check your email configuration.'}`
          );
        } else if (data.failed > 0) {
          // Some emails failed
          showMessage(
            "info",
            `Partially sent: ${data.sent} succeeded, ${data.failed} failed`
          );
        } else {
          // All succeeded
          showMessage(
            "success",
            `Email sent successfully to ${data.sent} recipient(s)!`
          );
        }
        
        // Clear form on any success (even partial)
        if (data.sent > 0) {
          setCustomEmailSubject("");
          setCustomEmailBody("");
          setCustomEmailTargetType("all");
          setCustomEmailSelectedPlayers([]);
          setCustomEmailDivision(null);
          setCustomEmailGuestFilter("all");
        }
        
        // Refresh usage stats
        fetchEmailUsage();
      } else {
        const error = await response.json();
        showMessage("error", error.error || "Failed to send email");
      }
    } catch (error) {
      showMessage("error", "Network error: Failed to send email. Please check your connection.");
      console.error("Error sending custom email:", error);
    } finally {
      setSendingCustomEmail(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <EmailIcon sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4">Email Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure SMTP settings and email notification preferences
          </Typography>
        </Box>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Card>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ 
            borderBottom: 1, 
            borderColor: "divider",
            '& .MuiTab-root': {
              minWidth: { xs: 80, sm: 120 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 }
            }
          }}
        >
          <Tab label="SMTP Config" />
          <Tab label="Preferences" />
          <Tab label="Templates" />
          <Tab label="Custom" />
          <Tab label="Opt-outs" />
        </Tabs>

        <CardContent>
          <TabPanel value={tabValue} index={0}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Email Service Status
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.enabled}
                      onChange={(e) =>
                        setConfig({ ...config, enabled: e.target.checked })
                      }
                    />
                  }
                  label={
                    config.enabled
                      ? "Email notifications enabled"
                      : "Email notifications disabled"
                  }
                />
                {!config.configured && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Email is not configured yet. Enter your SMTP settings below
                    to get started.
                  </Alert>
                )}
                
                {!config.enabled && config.configured && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Email notifications are currently disabled. Enable the switch above and save your configuration to start sending emails.
                  </Alert>
                )}
                
                {usageStats && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Daily Email Usage
                    </Typography>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        bgcolor: usageStats.atLimit ? 'error.light' : usageStats.nearLimit ? 'warning.light' : 'success.light',
                        border: 1,
                        borderColor: usageStats.atLimit ? 'error.main' : usageStats.nearLimit ? 'warning.main' : 'success.main'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          {usageStats.count} / {usageStats.limit}
                        </Typography>
                        <Chip 
                          label={`${usageStats.percentUsed}%`}
                          color={usageStats.atLimit ? 'error' : usageStats.nearLimit ? 'warning' : 'success'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {usageStats.limitType} • {usageStats.remaining} emails remaining today
                      </Typography>
                      {usageStats.nearLimit && (
                        <Alert severity={usageStats.atLimit ? 'error' : 'warning'} sx={{ mt: 2 }}>
                          {usageStats.atLimit 
                            ? '⚠️ Daily limit reached! Emails will not be sent until tomorrow.'
                            : '⚠️ Approaching daily limit. Consider upgrading your plan or switching to a provider with higher limits.'}
                        </Alert>
                      )}
                    </Paper>
                  </Box>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Email Provider
                </Typography>
                <Select
                  fullWidth
                  value={config.provider}
                  onChange={(e) =>
                    setConfig({ ...config, provider: e.target.value as 'smtp' | 'resend' | 'aws-ses' | 'brevo' })
                  }
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="brevo">Brevo (300 emails/day free - Recommended)</MenuItem>
                  <MenuItem value="resend">Resend (100 emails/day free)</MenuItem>
                  <MenuItem value="aws-ses">AWS SES (62,000 emails/month - Complex setup)</MenuItem>
                  <MenuItem value="smtp">SMTP (Gmail / Custom - May be blocked)</MenuItem>
                </Select>

                {config.provider === 'brevo' && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Brevo works reliably on cloud platforms like Railway. Get your free API key at{" "}
                    <a href="https://www.brevo.com" target="_blank" rel="noopener noreferrer">
                      brevo.com
                    </a>
                    {" "}(300 emails/day free, 9,000/month - Best free tier!)
                  </Alert>
                )}

                {config.provider === 'resend' && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Resend works reliably on cloud platforms like Railway. Get your free API key at{" "}
                    <a href="https://resend.com" target="_blank" rel="noopener noreferrer">
                      resend.com
                    </a>
                    {" "}(100 emails/day free, 3,000/month with credit card)
                  </Alert>
                )}

                {config.provider === 'aws-ses' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    ⚡ AWS SES offers 62,000 emails/month free. Requires AWS account and email/domain verification.{" "}
                    <a href="https://aws.amazon.com/ses/" target="_blank" rel="noopener noreferrer">
                      Learn more
                    </a>
                  </Alert>
                )}

                {config.provider === 'smtp' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    ⚠️ SMTP may be blocked on some cloud platforms like Railway. Use Resend or AWS SES if emails fail.
                  </Alert>
                )}
              </Box>

              <Divider />

              {config.provider === 'brevo' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Brevo API Settings
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Brevo API Key"
                      type={showPassword ? "text" : "password"}
                      value={brevoApiKey}
                      onChange={(e) => setBrevoApiKey(e.target.value)}
                      placeholder="xkeysib-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      helperText={
                        config.hasBrevoApiKey && !brevoApiKey
                          ? "API key is configured. Leave empty to keep existing key."
                          : "Get your API key from app.brevo.com (Account → SMTP & API → API Keys)"
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </Box>
              )}

              {config.provider === 'aws-ses' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    AWS SES Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Create IAM credentials with SES sending permissions. Verify your from email/domain in AWS SES console.
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="AWS Access Key ID"
                      value={awsAccessKeyId}
                      onChange={(e) => setAwsAccessKeyId(e.target.value)}
                      placeholder="AKIAIOSFODNN7EXAMPLE"
                      helperText="IAM user access key with SES permissions"
                    />
                    <TextField
                      fullWidth
                      label="AWS Secret Access Key"
                      type={showPassword ? "text" : "password"}
                      value={awsSecretAccessKey}
                      onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                      placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                      helperText="IAM user secret key (kept secure)"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="AWS Region"
                      value={awsRegion}
                      onChange={(e) => setAwsRegion(e.target.value)}
                      placeholder="us-east-1"
                      helperText="AWS region where SES is configured (e.g., us-east-1, eu-west-1)"
                    />
                  </Stack>
                </Box>
              )}

              {config.provider === 'resend' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Resend API Settings
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Resend API Key"
                      type={showPassword ? "text" : "password"}
                      value={resendApiKey}
                      onChange={(e) => setResendApiKey(e.target.value)}
                      placeholder="re_xxxxxxxxxxxx"
                      helperText={
                        config.hasResendApiKey && !resendApiKey
                          ? "API key is configured. Leave empty to keep existing key."
                          : "Get your API key from resend.com/api-keys"
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </Box>
              )}

              {config.provider === 'aws-ses' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    AWS SES Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Create IAM credentials with SES sending permissions. Verify your from email/domain in AWS SES console.
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="AWS Access Key ID"
                      value={awsAccessKeyId}
                      onChange={(e) => setAwsAccessKeyId(e.target.value)}
                      placeholder="AKIAIOSFODNN7EXAMPLE"
                      helperText="IAM user access key with SES permissions"
                    />
                    <TextField
                      fullWidth
                      label="AWS Secret Access Key"
                      type={showPassword ? "text" : "password"}
                      value={awsSecretAccessKey}
                      onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                      placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                      helperText="IAM user secret key (kept secure)"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="AWS Region"
                      value={awsRegion}
                      onChange={(e) => setAwsRegion(e.target.value)}
                      placeholder="us-east-1"
                      helperText="AWS region where SES is configured (e.g., us-east-1, eu-west-1)"
                    />
                  </Stack>
                </Box>
              )}

              {config.provider === 'smtp' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    SMTP Settings (Gmail)
                  </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  For Gmail, use smtp.gmail.com and generate an App Password at{" "}
                  <a
                    href="https://myaccount.google.com/apppasswords"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    myaccount.google.com/apppasswords
                  </a>
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="SMTP Host"
                    value={config.smtpHost}
                    onChange={(e) =>
                      setConfig({ ...config, smtpHost: e.target.value })
                    }
                    placeholder="smtp.gmail.com"
                  />

                  <TextField
                    fullWidth
                    type="number"
                    label="SMTP Port"
                    value={config.smtpPort}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        smtpPort: parseInt(e.target.value) || 587,
                      })
                    }
                    helperText="Use 587 for TLS"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.smtpSecure}
                        onChange={(e) =>
                          setConfig({ ...config, smtpSecure: e.target.checked })
                        }
                      />
                    }
                    label="Use SSL (port 465)"
                  />

                  <TextField
                    fullWidth
                    label="SMTP User (Email)"
                    value={config.smtpUser}
                    onChange={(e) =>
                      setConfig({ ...config, smtpUser: e.target.value })
                    }
                    placeholder="your-email@gmail.com"
                    type="email"
                  />

                  <TextField
                    fullWidth
                    label="SMTP Password (App Password)"
                    type={showPassword ? "text" : "password"}
                    value={smtpPass}
                    onChange={(e) => setSmtpPass(e.target.value)}
                    placeholder="Enter app password to update"
                    helperText="Leave empty to keep existing password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Sender Information
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="From Email"
                    value={config.fromEmail}
                    onChange={(e) =>
                      setConfig({ ...config, fromEmail: e.target.value })
                    }
                    placeholder="noreply@padeleagle.com"
                    type="email"
                    helperText="Email address that appears in the 'From' field"
                  />

                  <TextField
                    fullWidth
                    label="From Name"
                    value={config.fromName}
                    onChange={(e) =>
                      setConfig({ ...config, fromName: e.target.value })
                    }
                    placeholder="Padel Eagle"
                    helperText="Name that appears in the 'From' field"
                  />

                  <TextField
                    fullWidth
                    label="Reply-To Email (Optional)"
                    value={config.replyToEmail || ""}
                    onChange={(e) =>
                      setConfig({ ...config, replyToEmail: e.target.value })
                    }
                    placeholder="admin@padeleagle.com"
                    type="email"
                    helperText="Optional: Email address for replies"
                  />
                </Stack>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveConfig}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
                  size="small"
                  sx={{ flex: { xs: '1 1 100%', sm: 'initial' } }}
                >
                  Save Configuration
                </Button>
                <Tooltip 
                  title={!config.enabled ? "Enable email notifications and save configuration first" : ""}
                  arrow
                >
                  <span>
                    <Button
                      variant="outlined"
                      onClick={() => setTestDialogOpen(true)}
                      startIcon={<SendIcon />}
                      disabled={!config.enabled}
                      size="small"
                      sx={{ flex: { xs: '1 1 100%', sm: 'initial' } }}
                    >
                      Send Test Email
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip 
                  title="Clear cached email connection. Use this if you changed settings or experiencing connection issues."
                  arrow
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleClearCache}
                    size="small"
                    sx={{ flex: { xs: '1 1 100%', sm: 'initial' } }}
                  >
                    Clear Cache
                  </Button>
                </Tooltip>
                <Tooltip 
                  title="Test which SMTP ports are accessible from the server"
                  arrow
                >
                  <Button
                    variant="outlined"
                    onClick={handleDiagnose}
                    size="small"
                    sx={{ flex: { xs: '1 1 100%', sm: 'initial' } }}
                  >
                    Diagnose Connection
                  </Button>
                </Tooltip>
              </Box>
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Tournament Emails
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Automatic emails sent to players during tournament registration
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendTournamentInvitation ?? true}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendTournamentInvitation: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography fontWeight={600}>Tournament Invite — Notify invitee + send response to inviter</Typography>
                        <Typography variant="body2" color="text.secondary">
                          When a player invites an existing player to a team, the invitee receives an email with one-click Accept / Decline buttons. The inviter is then automatically notified of the response.
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendTournamentConfirmation}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendTournamentConfirmation: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>
                          Tournament Confirmation - When a partner accepts and the team is confirmed
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendTournamentWaitlist}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendTournamentWaitlist: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>
                          Waitlist Notification - When a team is added to the waitlist
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendWaitlistPromotion}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendWaitlistPromotion: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: 'error.main' }}>
                          Waitlist Promotion - When team moves from waitlist to confirmed <strong>(Not implemented yet)</strong>
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendTournamentReminder}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendTournamentReminder: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography sx={{ color: 'error.main' }}>
                        Tournament Reminder - Day before tournament <strong>(Not implemented yet)</strong>
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendTournamentResults}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendTournamentResults: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography sx={{ color: 'error.main' }}>
                        Tournament Results - After tournament completion <strong>(Not implemented yet)</strong>
                      </Typography>
                    }
                  />
                </FormGroup>
              </Box>

              {!isTournamentOrg && (
                <>
              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  League Day Emails
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Automatic emails for league day registrations
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendLeagueDayConfirmation}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendLeagueDayConfirmation: e.target.checked,
                          })
                        }
                      />
                    }
                    label="League Day Confirmation - When player registers"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendLeagueDayReminder}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendLeagueDayReminder: e.target.checked,
                          })
                        }
                      />
                    }
                    label="League Day Reminder - Day before league day, sent automatically at 9am"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendLeagueDayResults}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendLeagueDayResults: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography sx={{ color: 'error.main' }}>
                        League Day Results - After league day completion <strong>(Not implemented yet)</strong>
                      </Typography>
                    }
                  />
                </FormGroup>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Payment Reminder Emails
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Sent automatically when a league day completes to any participant with outstanding league days to pay. Also sendable manually from the Finances page.
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendPaymentReminder}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendPaymentReminder: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Payment Reminder - When a player has outstanding league days after a league day completes"
                  />
                </FormGroup>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Season Registration Emails
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Automatic emails for season registration interest
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendSeasonRegistrationConfirmation}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendSeasonRegistrationConfirmation: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>
                          Season Registration Confirmation - When someone registers interest in new season
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            showInfoDialog(
                              "Season Registration Confirmation",
                              "When enabled, an automatic confirmation email is sent to anyone who fills out the season registration form.\n\nThe email includes:\n• Confirmation of their registration\n• Their submitted details\n• Information about next steps\n\nThis helps players know their interest has been received."
                            )
                          }
                        >
                          <InfoIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Box>
                    }
                  />
                </FormGroup>
              </Box>

                </>
              )}

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  General Emails
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendWelcomeEmail}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendWelcomeEmail: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>
                          Welcome Email - When new player account is approved by admin
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            showInfoDialog(
                              "Welcome Email",
                              "When enabled, this email is automatically sent when an admin approves a new player account request.\n\nThe email includes:\n• Welcome message\n• Username confirmation\n• Division assignment\n\nThis helps new players know their account is ready to use."
                            )
                          }
                        >
                          <InfoIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.sendPasswordReset}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            sendPasswordReset: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography>
                        Password Reset - For account recovery
                      </Typography>
                    }
                  />
                </FormGroup>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Admin Notifications
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.notifyAdminOnRegistration}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          notifyAdminOnRegistration: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>
                        Notify admin when new players register
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          showInfoDialog(
                            "Admin Registration Notification",
                            "When enabled, an email notification is sent to the specified admin email address whenever a new player creates an account and requests approval.\n\nThe email includes:\n• Player name, username, and email\n• Phone and skill level (if provided)\n• Direct link to review the request in the Admin Panel\n\nThis helps you stay informed of new registration requests and respond quickly to approve or reject accounts."
                          )
                        }
                      >
                        <InfoIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Box>
                  }
                />
                {preferences.notifyAdminOnRegistration && (
                  <TextField
                    fullWidth
                    label="Admin Notification Email"
                    value={preferences.adminNotificationEmail || ""}
                    onChange={(e) =>
                      setPreferences({
                        ...preferences,
                        adminNotificationEmail: e.target.value,
                      })
                    }
                    placeholder="admin@yourdomain.com"
                    type="email"
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleSavePreferences}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
                size="small"
                fullWidth={false}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Save Preferences
              </Button>
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Customize Email Templates
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Customize the emails sent to your players. Use variables like{" "}
                  <code>{"{{player.name}}"}</code> to personalize messages.
                </Typography>
              </Box>

              <FormControl fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  Select Template
                </Typography>
                <Select
                  value={selectedTemplateType}
                  onChange={handleTemplateTypeChange}
                >
                  {visibleTemplateLabels.map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loadingTemplate ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : currentTemplate ? (
                <>
                  {currentTemplate.description && (
                    <Alert severity="info">{currentTemplate.description}</Alert>
                  )}

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Available Variables
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {currentTemplate.availableVariables?.map((variable) => (
                        <Chip
                          key={variable}
                          label={`{{${variable}}}`}
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${variable}}}`);
                            showMessage("info", "Variable copied to clipboard");
                          }}
                          sx={{ cursor: "pointer" }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Divider />

                  <TextField
                    fullWidth
                    label="Email Subject"
                    value={currentTemplate.subject}
                    onChange={(e) =>
                      setCurrentTemplate({
                        ...currentTemplate,
                        subject: e.target.value,
                      })
                    }
                    placeholder="Use {{variables}} in your subject"
                  />

                  <TextField
                    fullWidth
                    label="Email Body (HTML)"
                    value={currentTemplate.htmlBody}
                    onChange={(e) =>
                      setCurrentTemplate({
                        ...currentTemplate,
                        htmlBody: e.target.value,
                      })
                    }
                    multiline
                    rows={15}
                    placeholder="Use {{variables}} in your email body. HTML tags are supported."
                    sx={{ fontFamily: "monospace" }}
                  />

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveTemplate}
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
                    >
                      Save Template
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setPreviewDialogOpen(true)}
                      startIcon={<PreviewIcon />}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleResetTemplate}
                      startIcon={<RefreshIcon />}
                      disabled={saving}
                    >
                      Reset to Default
                    </Button>
                  </Box>
                </>
              ) : (
                <Alert severity="error">Failed to load template</Alert>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Stack spacing={3}>
              <Alert severity="info">
                <Typography variant="body2">
                  Compose and send custom emails to players. You can target all players, specific divisions, or select individual players.
                </Typography>
              </Alert>

              {/* Warning if many players don't have emails */}
              {allPlayers.length > 0 && allPlayers.filter((p: any) => p.email).length < allPlayers.length * 0.5 && (
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>⚠️ Low Email Coverage:</strong> Only {allPlayers.filter((p: any) => p.email).length} out of {allPlayers.length} active players have email addresses. 
                    Consider collecting emails from players to improve communication.
                  </Typography>
                </Alert>
              )}

              {/* Email Usage Display */}
              {usageStats && (
                <Paper sx={{ p: 2, bgcolor: usageStats.atLimit ? "error.light" : usageStats.nearLimit ? "warning.light" : "success.light" }}>
                  <Typography variant="body2">
                    <strong>Daily Email Usage:</strong> {usageStats.count} / {usageStats.limit} ({usageStats.percentUsed}%)
                    {usageStats.atLimit && " - Daily limit reached!"}
                    {usageStats.nearLimit && !usageStats.atLimit && " - Approaching limit"}
                  </Typography>
                </Paper>
              )}

              <Box>
                <Typography variant="h6" gutterBottom>
                  Email Content
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Start with a template or write your own message. Use variables like <code>{"{{player.name}}"}</code> to personalize.
                </Typography>
                
                {/* Template Selector */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Load from Template (Optional)
                  </Typography>
                  <Select
                    value={customEmailTemplateType}
                    onChange={(e) => handleCustomEmailTemplateChange(e.target.value as EmailTemplateType | "")}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Start with blank email</em>
                    </MenuItem>
                    <MenuItem disabled>
                      <em>── Load Template ──</em>
                    </MenuItem>
                    {visibleTemplateLabels.map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Select a template to pre-fill subject and message. Variables will be replaced with each player's data when sending.
                  </Typography>
                </FormControl>

                {/* Available Variables Hint */}
                {customEmailTemplateType && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Available Variables:</strong> Click any variable to copy it
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {["player.name", "player.email", "player.phone", "organization.name"].map((variable) => (
                        <Chip
                          key={variable}
                          label={`{{${variable}}}`}
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${variable}}}`);
                            showMessage("info", "Variable copied to clipboard");
                          }}
                          sx={{ cursor: "pointer" }}
                        />
                      ))}
                    </Box>
                  </Alert>
                )}
                
                <TextField
                  fullWidth
                  label="Subject"
                  value={customEmailSubject}
                  onChange={(e) => setCustomEmailSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Message"
                  value={customEmailBody}
                  onChange={(e) => setCustomEmailBody(e.target.value)}
                  multiline
                  rows={10}
                  placeholder="Enter your message here... Use {{player.name}} and other variables for personalization"
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Target Recipients
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Select
                    value={customEmailTargetType}
                    onChange={(e) => {
                      const newType = e.target.value as "all" | "specific" | "division";
                      setCustomEmailTargetType(newType);
                      // Reset selections when changing type
                      setCustomEmailSelectedPlayers([]);
                      setCustomEmailDivision(null);
                    }}
                  >
                    <MenuItem value="all">All Players (with emails)</MenuItem>
                    <MenuItem value="division">Specific Division (only players with emails)</MenuItem>
                    <MenuItem value="specific">Select Specific Players</MenuItem>
                  </Select>
                  {customEmailTargetType === "all" && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Will send to all active players who have email addresses
                    </Typography>
                  )}
                </FormControl>

                {/* Guest Filter (applies to all and division) */}
                {(customEmailTargetType === "all" || customEmailTargetType === "division") && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Select
                      value={customEmailGuestFilter}
                      onChange={(e) => setCustomEmailGuestFilter(e.target.value as "all" | "guestOnly" | "nonGuestOnly")}
                    >
                      <MenuItem value="all">All Players</MenuItem>
                      <MenuItem value="guestOnly">Guest Players Only</MenuItem>
                      <MenuItem value="nonGuestOnly">Non-Guest Players Only</MenuItem>
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Filter by guest player status
                    </Typography>
                  </FormControl>
                )}

                {customEmailTargetType === "division" && (
                  <Box>
                    <Autocomplete
                      fullWidth
                      options={divisions}
                      value={customEmailDivision}
                      onChange={(event, newValue) => setCustomEmailDivision(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Division"
                          placeholder="Type to search divisions..."
                          helperText="Only players with email addresses will receive the email"
                        />
                      )}
                    />
                  </Box>
                )}

                {customEmailTargetType === "specific" && (
                  <Autocomplete
                    multiple
                    fullWidth
                    options={allPlayers}
                    value={customEmailSelectedPlayers}
                    onChange={(event, newValue) => setCustomEmailSelectedPlayers(newValue)}
                    getOptionLabel={(option) => 
                      option.email 
                        ? `${option.name} (${option.email})` 
                        : `${option.name} (No Email)`
                    }
                    getOptionDisabled={(option) => !option.email}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Players"
                        placeholder="Type to search players..."
                        helperText="Players without email addresses are disabled"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          key={option.id}
                        />
                      ))
                    }
                  />
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={handlePreviewCustomEmail}
                  startIcon={loadingPreview ? <CircularProgress size={20} /> : <PreviewIcon />}
                  disabled={
                    loadingPreview ||
                    !customEmailSubject ||
                    !customEmailBody ||
                    (customEmailTargetType === "division" && !customEmailDivision) ||
                    (customEmailTargetType === "specific" && customEmailSelectedPlayers.length === 0)
                  }
                >
                  Preview Recipients
                </Button>
              </Box>
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Players who chose not to receive certain emails (set in their dashboard under
                Profile &amp; Settings → Email Preferences). These players are skipped
                automatically when those emails are sent.
              </Typography>
              {optOutsLoading ? (
                <CircularProgress size={24} />
              ) : optOutPlayers.length === 0 ? (
                <Alert severity="info">No players have opted out of any emails.</Alert>
              ) : (
                optOutPlayers.map((p) => (
                  <Box
                    key={p.id}
                    sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {p.name}
                      {p.division ? ` · ${p.division}` : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {p.email || "No email"}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {p.optedOut.map((t) => (
                        <Chip key={t} size="small" label={t.replace(/_/g, " ")} />
                      ))}
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a test email to verify your SMTP configuration is working correctly.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Test Email Address"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="your-email@example.com"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendTestEmail}
            variant="contained"
            disabled={sendingTest || !testEmail}
            startIcon={sendingTest ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Send Test
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog 
        open={infoDialogOpen} 
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{infoDialogContent.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {infoDialogContent.description}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent>
          <Paper elevation={3} sx={{ p: 2, mb: 2, bgcolor: "grey.100" }}>
            <Typography variant="subtitle2" color="text.secondary">
              Subject:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {currentTemplate?.subject}
            </Typography>
          </Paper>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Email Body:
            </Typography>
            <Box
              dangerouslySetInnerHTML={{ __html: (currentTemplate?.htmlBody || "").split("{{app.frontendUrl}}").join(window.location.origin) }}
              sx={{ 
                "& *": { maxWidth: "100%" },
                overflowX: "auto"
              }}
            />
          </Paper>
          <Alert severity="info" sx={{ mt: 2 }}>
            This is a preview with variables shown as-is. In actual emails, variables like{" "}
            <code>{"{{player.name}}"}</code> will be replaced with real data.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Email Preview Dialog */}
      <Dialog 
        open={customEmailPreviewOpen} 
        onClose={() => setCustomEmailPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview & Send Custom Email</DialogTitle>
        <DialogContent>
          {/* Email Preview */}
          <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: "grey.100" }}>
            <Typography variant="subtitle2" color="text.secondary">
              Subject:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold", mb: 2 }}>
              {customEmailSubject}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Message:
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ whiteSpace: "pre-wrap", mt: 1 }}
            >
              {customEmailBody}
            </Typography>
          </Paper>

          {/* Recipients List */}
          <Typography variant="h6" gutterBottom>
            Recipients ({customEmailRecipients.length})
          </Typography>
          
          {customEmailRecipients.length > 0 ? (
            <Paper 
              elevation={1} 
              sx={{ 
                maxHeight: 300, 
                overflow: "auto", 
                p: 2,
                bgcolor: "background.default"
              }}
            >
              <Stack spacing={1}>
                {customEmailRecipients.map((recipient) => (
                  <Box 
                    key={recipient.id}
                    sx={{ 
                      display: "flex", 
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1,
                      borderRadius: 1,
                      bgcolor: "background.paper"
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {recipient.name}
                        {recipient.isGuest && (
                          <Chip 
                            label="Guest" 
                            size="small" 
                            color="secondary"
                            sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {recipient.email}
                      </Typography>
                    </Box>
                    <Chip 
                      label={recipient.division} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Stack>
            </Paper>
          ) : (
            <Alert severity="warning">No recipients found matching the criteria</Alert>
          )}

          {usageStats && (
            <Alert 
              severity={usageStats.atLimit ? "error" : usageStats.nearLimit ? "warning" : "info"} 
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                Current usage: {usageStats.count} / {usageStats.limit} emails today
                <br />
                After sending: {usageStats.count + customEmailRecipients.length} / {usageStats.limit}
                {usageStats.count + customEmailRecipients.length > usageStats.limit && 
                  " (⚠️ This will exceed your daily limit!)"}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomEmailPreviewOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSendCustomEmail}
            variant="contained"
            color="primary"
            disabled={
              sendingCustomEmail || 
              customEmailRecipients.length === 0 ||
              (usageStats?.atLimit && customEmailRecipients.length > 0)
            }
            startIcon={sendingCustomEmail ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingCustomEmail ? "Sending..." : `Send to ${customEmailRecipients.length} Recipients`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
